// src/App.tsx
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import jwtDecode from 'jwt-decode'; // Keep the default import
import Login from './components/Login';
import Register from './components/Register';
import Dashboard from './components/Dashboard';
import Templates from './components/Templates';
import ClientForm from './components/ClientForm';
import CreateForm from './components/CreateForm';
import ViewForms from './components/ViewForms';
import EditTemplate from './components/EditTemplate';
import ViewFormDetails from './components/ViewFormDetails';
import Clients from './components/Clients';
import ClientDetail from './components/ClientDetail';
import Venues from './components/Venues';
import NavBar from './components/NavBar';
import VenueDetail from './components/VenueDetail';
import ClientLogin from './components/ClientLogin';
import ClientDashboard from './components/ClientDashboard';
import ClientNavBar from './components/ClientNavBar';
import ClientForms from './components/ClientForms';
import ClientAccount from './components/ClientAccount';
import CreateContract from './components/CreateContract';
import ClientContracts from './components/ClientContracts';
import ClientContract from './components/ClientContract';
import ViewContracts from './components/ViewContracts';
import ContractTemplates from './components/ContractTemplates';
import EditContractTemplate from './components/EditContractTemplate';
import ViewContractDetails from './components/ViewContractDetails';

interface JwtPayload {
  'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name': string; // Username claim
}

const App: React.FC = () => {
  const [isDjLoggedIn, setIsDjLoggedIn] = useState(!!localStorage.getItem('token'));
  const [isClientLoggedIn, setIsClientLoggedIn] = useState(!!localStorage.getItem('clientToken'));
  const [djUsername, setDjUsername] = useState<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    const checkDjLoginStatus = () => {
      const token = localStorage.getItem('token');
      setIsDjLoggedIn(!!token);
      if (token) {
        try {
          const decoded: JwtPayload = jwtDecode(token); // Explicitly call jwtDecode as a function
          setDjUsername(decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name']);
        } catch (error) {
          console.error('Failed to decode token:', error);
          setDjUsername(null);
        }
      } else {
        setDjUsername(null);
      }
      console.log('DJ Login Status:', !!token, 'Token:', token, 'Username:', djUsername);
    };
    const checkClientLoginStatus = () => {
      const clientToken = localStorage.getItem('clientToken');
      setIsClientLoggedIn(!!clientToken);
      console.log('Client Login Status:', !!clientToken, 'Client Token:', clientToken);
    };

    checkDjLoginStatus();
    checkClientLoginStatus();
    window.addEventListener('storage', checkDjLoginStatus);
    window.addEventListener('authChange', checkDjLoginStatus);
    window.addEventListener('clientAuthChange', checkClientLoginStatus);

    return () => {
      window.removeEventListener('storage', checkDjLoginStatus);
      window.removeEventListener('authChange', checkDjLoginStatus);
      window.removeEventListener('clientAuthChange', checkClientLoginStatus);
    };
  }, []);

  const isDjRoute =
    location.pathname.startsWith('/dashboard') ||
    location.pathname.startsWith('/templates') ||
    location.pathname.startsWith('/contract-templates') ||
    location.pathname.startsWith('/create-form') ||
    location.pathname.startsWith('/view-forms') ||
    location.pathname.startsWith('/edit-template') ||
    location.pathname.startsWith('/edit-contract-template') ||
    location.pathname.startsWith('/form-details') ||
    location.pathname.startsWith('/contract-details') ||
    location.pathname.startsWith('/clients') ||
    location.pathname.startsWith('/venues') ||
    location.pathname.startsWith('/create-contract') ||
    location.pathname.startsWith('/view-contracts') ||
    location.pathname === '/register' ||
    location.pathname === '/';

  const isClientRoute =
    location.pathname.startsWith('/client') ||
    location.pathname.startsWith('/form') ||
    location.pathname.startsWith('/contract');

  console.log('Current Path:', location.pathname, 'isClientRoute:', isClientRoute, 'isClientLoggedIn:', isClientLoggedIn);

  return (
    <div>
      <div className="d-flex justify-content-center py-2">
        <img 
          src="/staysaillogo.png" 
          alt="Staysail CRM & Planning" 
          className="img-fluid" 
          style={{ 
            width: "300px",
            height: "auto",
          }} 
        />
      </div>

      {isDjLoggedIn && isDjRoute && <NavBar />}
      {isClientLoggedIn && isClientRoute && <ClientNavBar />}
      <Routes>
        <Route path="/" element={<Login />} />
        <Route
          path="/register"
          element={
            isDjLoggedIn && djUsername === 'jkupis' ? <Register /> : <Navigate to="/" />
          }
        />
        <Route
          path="/dashboard"
          element={isDjLoggedIn ? <Dashboard /> : <Navigate to="/" />}
        />
        <Route
          path="/templates"
          element={isDjLoggedIn ? <Templates /> : <Navigate to="/" />}
        />
        <Route
          path="/contract-templates"
          element={isDjLoggedIn ? <ContractTemplates /> : <Navigate to="/" />}
        />
        <Route
          path="/create-form"
          element={isDjLoggedIn ? <CreateForm /> : <Navigate to="/" />}
        />
        <Route
          path="/view-forms"
          element={isDjLoggedIn ? <ViewForms /> : <Navigate to="/" />}
        />
        <Route
          path="/edit-template/:id"
          element={isDjLoggedIn ? <EditTemplate /> : <Navigate to="/" />}
        />
        <Route
          path="/edit-contract-template/:id"
          element={isDjLoggedIn ? <EditContractTemplate /> : <Navigate to="/" />}
        />
        <Route
          path="/form-details/:id"
          element={isDjLoggedIn ? <ViewFormDetails /> : <Navigate to="/" />}
        />
        <Route
          path="/contract-details/:id"
          element={isDjLoggedIn ? <ViewContractDetails /> : <Navigate to="/" />}
        />
        <Route
          path="/clients"
          element={isDjLoggedIn ? <Clients /> : <Navigate to="/" />}
        />
        <Route
          path="/clients/:id"
          element={isDjLoggedIn ? <ClientDetail /> : <Navigate to="/" />}
        />
        <Route
          path="/venues/:id"
          element={isDjLoggedIn ? <VenueDetail /> : <Navigate to="/" />}
        />
        <Route
          path="/venues"
          element={isDjLoggedIn ? <Venues /> : <Navigate to="/" />}
        />
        <Route
          path="/create-contract"
          element={isDjLoggedIn ? <CreateContract /> : <Navigate to="/" />}
        />
        <Route
          path="/view-contracts"
          element={isDjLoggedIn ? <ViewContracts /> : <Navigate to="/" />}
        />
        <Route path="/client/login" element={<ClientLogin />} />
        <Route
          path="/client/dashboard"
          element={isClientLoggedIn ? <ClientDashboard /> : <Navigate to="/client/login" />}
        />
        <Route
          path="/client/forms"
          element={isClientLoggedIn ? <ClientForms /> : <Navigate to="/client/login" />}
        />
        <Route
          path="/client/account"
          element={isClientLoggedIn ? <ClientAccount /> : <Navigate to="/client/login" />}
        />
        <Route
          path="/client/contracts"
          element={isClientLoggedIn ? <ClientContracts /> : <Navigate to="/client/login" />}
        />
        <Route
          path="/client/contract/:id"
          element={isClientLoggedIn ? <ClientContract /> : <Navigate to="/client/login" />}
        />
        <Route
          path="/form/:id"
          element={isClientLoggedIn ? <ClientForm /> : <Navigate to="/client/login" />}
        />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </div>
  );
};

const AppWrapper: React.FC = () => (
  <Router>
    <App />
  </Router>
);

export default AppWrapper;